import React from 'react';
import { Button, Card, Space, Typography, message } from 'antd';
import { Link } from 'react-router';
import type { Proposal } from '../../types/proposal';
import { ProposalStatusTag } from './ProposalStatusTag';
import { ShareAltOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

interface ProposalCardProps {
  proposal: Proposal;
  viewPath: string;
  editPath?: string;
}

export const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, viewPath, editPath }) => {
  const address = proposal.mapResult?.map?.address || 'Address pending';
  const snapshot = proposal.mapResult?.snapshot || null;
  const shareUrl = `${window.location.origin}/proposal/${proposal.id}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Proposal ${proposal.id.slice(-6).toUpperCase()}`,
          url: shareUrl,
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        message.success('Link copied to clipboard');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        message.error('Failed to copy link');
      }
    } else {
      message.error('Sharing not supported');
    }
  };

  const actions = [
    <Link key={`view-${proposal.id}`} to={viewPath}>
      <Button type="link">View</Button>
    </Link>,
    editPath ? (
      <Link key={`edit-${proposal.id}`} to={editPath}>
        <Button type="link">Edit</Button>
      </Link>
    ) : null,
    <Button
      key={`share-${proposal.id}`}
      type="link"
      icon={<ShareAltOutlined />}
      onClick={handleShare}>
      Share
    </Button>,
  ].filter(Boolean);

  return (
    <Card hoverable actions={actions}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {snapshot && (
          <div
            style={{
              width: '100%',
              height: 200,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
            }}>
            <img
              src={snapshot}
              alt="Map preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        <Space direction="vertical" size={4}>
          <Title level={5} style={{ margin: 0 }}>
            Proposal {proposal.id.slice(-6).toUpperCase()}
          </Title>
          <Text type="secondary">{address}</Text>
        </Space>
        <Space size={12} align="center" wrap>
          {proposal.type !== 'STANDALONE' && <ProposalStatusTag status={proposal.status} />}
        </Space>
      </Space>
    </Card>
  );
};
